import { useState } from "react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";

interface MessageBubbleProps {
  role: "customer" | "agent" | "trainee" | "real-agent";
  text: string;
  label?: string;
  imageUrl?: string;
  timestamp?: string;
}

export function MessageBubble({ role, text, label, imageUrl, timestamp }: MessageBubbleProps) {
  const [lightbox, setLightbox] = useState(false);
  const isRight = role === "trainee" || role === "real-agent";

  const styles = {
    customer: { bg: "bg-gray-100 text-gray-900", avatar: "bg-orange-100 text-orange-600", letter: "C" },
    agent: { bg: "bg-blue-500 text-white", avatar: "bg-blue-100 text-blue-600", letter: "A" },
    trainee: { bg: "bg-green-500 text-white", avatar: "bg-green-100 text-green-600", letter: "Y" },
    "real-agent": { bg: "bg-blue-100 text-blue-800 border border-blue-200", avatar: "bg-blue-100 text-blue-600", letter: "R" },
  };

  const s = styles[role];

  return (
    <>
      <div className={cn("flex gap-3", isRight ? "flex-row-reverse" : "flex-row")}>
        <Avatar className="h-8 w-8 shrink-0">
          <AvatarFallback className={s.avatar}>{s.letter}</AvatarFallback>
        </Avatar>
        <div className={cn("max-w-[70%]", isRight ? "text-right" : "text-left")}>
          {(label || timestamp) && (
            <div className="mb-1">
              {label && <p className="text-xs text-gray-400">{label}</p>}
              {timestamp && (
                <p className="text-[10px] text-gray-400 opacity-70">
                  {new Date(timestamp).toLocaleString(undefined, {
                    month: "short",
                    day: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </p>
              )}
            </div>
          )}
          <div className={cn("rounded-lg px-4 py-2 inline-block text-left", s.bg)}>
            {imageUrl && (
              <img
                src={imageUrl}
                alt="Shared image"
                className="max-w-full rounded mb-1 cursor-pointer hover:opacity-80 transition-opacity"
                style={{ maxHeight: 200 }}
                onClick={() => setLightbox(true)}
              />
            )}
            {text && <p className="text-sm whitespace-pre-wrap">{text}</p>}
          </div>
        </div>
      </div>

      {lightbox && imageUrl && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 cursor-pointer"
          onClick={() => setLightbox(false)}
        >
          <img
            src={imageUrl}
            alt="Shared image"
            className="max-w-[90vw] max-h-[90vh] rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
}
